import { Head, useForm } from '@inertiajs/react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { FormEvent, useState } from 'react'

interface InviteProps {
  workspace: { slug: string; name: string }
  invitations: { id: number; email: string; role: string; token: string; created_at: string }[]
}

function Invite({ workspace, invitations }: InviteProps) {
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    role: 'editor',
  })

  function copyLink(inv: InviteProps['invitations'][0]) {
    const url = `${window.location.origin}/invite/${inv.token}`
    navigator.clipboard.writeText(url)
    setCopiedId(inv.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(`/${workspace.slug}/invitations`, {
      onSuccess: () => setData('email', ''),
    })
  }

  return (
    <>
      <Head title={`Invite - ${workspace.name}`} />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900">Invite Members</h2>
        <p className="mt-1 text-sm text-gray-500">
          Send email invitations to collaborate on {workspace.name}.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              type="email"
              id="email"
              value={data.email}
              onChange={(e) => setData('email', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="colleague@example.com"
              required
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Role
            </label>
            <select
              id="role"
              value={data.role}
              onChange={(e) => setData('role', e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={processing}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {processing ? 'Sending...' : 'Send Invitation'}
          </button>
        </form>

        {invitations.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900">Pending Invitations</h3>
            <ul className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                    <p className="text-xs text-gray-500">
                      {inv.role} — sent {new Date(inv.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => copyLink(inv)}
                    className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                  >
                    {copiedId === inv.id ? 'Copied!' : 'Copy link'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  )
}

Invite.layout = withWorkspaceLayout
export default Invite
